import { clickTab, selectProtocol, setReactInputValue, selectFirstReactSelectOption, openMenuAndGet } from '../../shared/domHelpers'
import { demoRoutes, setDemoTourResume, DEMO_MQTT_TOPIC_PUB, DEMO_MQTT_TOPIC_SUB } from '../DemoFlow'
import { DEMO_IMEI } from '../mock/fixtures/mqttPacket'
import demoStore from '../mock/DemoStore'
import { unlockDemoSaveButton, lockDemoSaveButton, withNativeDemoSaveAllowed } from '../demoSaveControls'
import { skipStageConfig } from '../demoSkipStage'

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))
const LISTA_READY = '[data-tour="grid-table"], [data-tour="grid-novo-btn"], [data-tour="help-equipamento-grid"]'

const getMenuListaEquipamentos = () =>
    openMenuAndGet('equipamento', 'group-equipamento', '[data-tour="menu-lista-equipamentos"]')

const fillImei = () => {
    const imeiInput = document.querySelector('[data-tour="form-imei"] input')
    if (imeiInput) setReactInputValue(imeiInput, DEMO_IMEI)
}

const fillFabricanteModelo = async () => {
    await selectFirstReactSelectOption('[data-tour="form-fabricante"]')
    await delay(200)
    await selectFirstReactSelectOption('[data-tour="form-modelo"]')
}

const fillOperadora = async () => {
    await selectFirstReactSelectOption('[data-tour="form-comunicacao"]')
}

const ensureStatusAtivo = () => {
    const statusAtivo = document.querySelector('[data-tour="form-status"] input[value="ativo"]')
    if (statusAtivo && !statusAtivo.checked) statusAtivo.click()
}

const fillTipoAcesso = async () => {
    ensureStatusAtivo()
    await delay(120)
    await selectFirstReactSelectOption('[data-tour="form-tipo-acesso"]')
}

const fillTimezone = async () => {
    await selectFirstReactSelectOption('[data-tour="form-timezone"]')
}

const selectMqttProtocol = async () => {
    clickTab('[data-tour="tab-protocolo"]')
    await delay(150)
    selectProtocol('mqtt')
}

const enableTopicosPersonalizados = () => {
    const checkbox = document.querySelector('#topico_personalizado')
    if (checkbox && !checkbox.checked) checkbox.click()
}

const fillTopicosMqtt = () => {
    enableTopicosPersonalizados()

    const pubInput = document.querySelector('[data-tour="mqtt-topico-publicar"] input')
    const subInput = document.querySelector('[data-tour="mqtt-topico-receber"] input')

    if (pubInput) {
        pubInput.readOnly = false
        pubInput.disabled = false
        setReactInputValue(pubInput, DEMO_MQTT_TOPIC_PUB)
    }

    if (subInput) {
        subInput.readOnly = false
        subInput.disabled = false
        setReactInputValue(subInput, DEMO_MQTT_TOPIC_SUB)
    }
}

const waitSuccessModal = (tourManager, timeout = 8000) =>
    tourManager.waitForElement(() => {
        const body = document.querySelector('.modal.show .modal-body, .modal.show .modal-content')
        if (body && /sucesso|criado|editado/i.test(body.textContent || '')) return body
        return null
    }, timeout)

const closeSuccessModalSoft = () => {
    const buttons = Array.from(document.querySelectorAll('.modal.show .modal-footer button, .modal.show button'))
    const continuar = buttons.find((button) => /^continuar$/i.test((button.textContent || '').trim()))
    if (continuar) {
        continuar.click()
        return
    }

    const fechar = buttons.find((button) => /^fechar$/i.test((button.textContent || '').trim()))
    if (fechar) {
        fechar.click()
        return
    }

    document.querySelector('.modal.show .close')?.click()
}

const ensureEquipamentoInStore = () => {
    if (!demoStore.state.equipamento) {
        demoStore.createEquipamento({
            imei: DEMO_IMEI,
            topico_personalizado: true,
            topico_publica: DEMO_MQTT_TOPIC_PUB,
            topico_recebe: DEMO_MQTT_TOPIC_SUB,
        })
    }
}

const goToParametrizacaoTour = async (tourManager) => {
    ensureEquipamentoInStore()
    setDemoTourResume('demo-parametrizacao', demoRoutes.equipamentoLista, 0)

    tourManager.internalNavigation = true

    try {
        if (tourManager.isActive()) tourManager.finish('demo-equipamento')

        if (tourManager.history) {
            tourManager.history.replace(demoRoutes.equipamentoLista)
            try {
                await tourManager.waitForElement(LISTA_READY, 8000)
            } catch (error) {}
            await delay(250)
            await tourManager.start('demo-parametrizacao', { resumeIndex: 0 })
            return
        }

        window.location.href = demoRoutes.equipamentoLista
    } finally {
        tourManager.internalNavigation = false
    }
}

const saveEquipamentoAndContinue = async (tourManager) => {
    if (tourManager._hdvSavingEquipamento) return
    tourManager._hdvSavingEquipamento = true

    const popoverNext = document.querySelector('.driver-popover-next-btn')
    const saveButton = document.querySelector('[data-tour="equipamento-salvar"]')

    const setBusy = (busy) => {
        if (popoverNext) {
            popoverNext.disabled = busy
            popoverNext.style.pointerEvents = busy ? 'none' : ''
            popoverNext.style.opacity = busy ? '0.6' : ''
        }
        const popoverPrev = document.querySelector('.driver-popover-prev-btn')
        if (popoverPrev) {
            popoverPrev.disabled = busy
            popoverPrev.style.pointerEvents = busy ? 'none' : ''
            popoverPrev.style.opacity = busy ? '0.55' : ''
            if (busy) popoverPrev.setAttribute('aria-disabled', 'true')
            else popoverPrev.removeAttribute('aria-disabled')
        }
        if (saveButton && busy) {
            saveButton.style.pointerEvents = 'none'
            saveButton.style.opacity = '0.6'
        }
    }

    setBusy(true)

    try {
        fillImei()
        await fillFabricanteModelo()
        await fillOperadora()
        ensureStatusAtivo()
        await fillTipoAcesso()
        await fillTimezone()
        await selectMqttProtocol()
        fillTopicosMqtt()
        await delay(100)

        ensureEquipamentoInStore()

        if (saveButton) {
            try {
                withNativeDemoSaveAllowed(saveButton, () => saveButton.click())
            } catch (error) {}
        }

        try {
            await waitSuccessModal(tourManager)
            await delay(400)
        } catch (error) {}

        if (document.querySelector('.modal.show')) {
            closeSuccessModalSoft()
            await delay(200)
        }

        await goToParametrizacaoTour(tourManager)
    } catch (error) {
        setBusy(false)
        if (saveButton) {
            unlockDemoSaveButton(saveButton)
            bindNativeEquipamentoSaveToTour(saveButton, tourManager)
        }
        await goToParametrizacaoTour(tourManager)
    } finally {
        tourManager._hdvSavingEquipamento = false
    }
}

const bindNativeEquipamentoSaveToTour = (button, tourManager) => {
    if (!button) return

    if (button._hdvDemoSaveHandler) {
        button.removeEventListener('click', button._hdvDemoSaveHandler, true)
        delete button._hdvDemoSaveHandler
    }

    unlockDemoSaveButton(button)

    const handler = (event) => {
        if (button._hdvAllowNativeSave) return
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation?.()
        saveEquipamentoAndContinue(tourManager)
    }

    button.addEventListener('click', handler, true)
    button._hdvDemoSaveHandler = handler
}

const unbindNativeEquipamentoSaveFromTour = (button) => {
    if (!button?._hdvDemoSaveHandler) return
    button.removeEventListener('click', button._hdvDemoSaveHandler, true)
    delete button._hdvDemoSaveHandler
    lockDemoSaveButton(button)
}

const buildDemoEquipamentoTourSteps = (tourManager) => [
    {
        skipStage: skipStageConfig('equipamentoListaIntro'),
        popover: {
            title: 'Modo demonstração iniciado',
            description:
                'Esta demonstração leva cerca de 3 minutos. Os cadastros serão preenchidos automaticamente com dados de exemplo. Basta clicar em <strong>Próximo</strong> para acompanhar cada etapa.<br><br>' +
                'Se preferir, clique em <strong>Pular etapa</strong> para ir direto ao cadastro de Equipamento.',
            side: 'over',
            align: 'center',
        },
    },
    {
        element: getMenuListaEquipamentos,
        ensureReady: () => {
            const menuItem = getMenuListaEquipamentos()
            if (menuItem) return Promise.resolve(menuItem)
            return tourManager.waitForElement(getMenuListaEquipamentos)
        },
        popover: {
            title: 'Lista de Equipamentos',
            description:
                'Este menu abre a lista de equipamentos. Clique em <strong>Próximo</strong> para acessá-la e iniciar o cadastro de um equipamento de exemplo.',
            side: 'right',
            align: 'start',
            onNextClick: () => {
                tourManager.navigateAndWait(demoRoutes.equipamentoLista, LISTA_READY, 2, 'demo-equipamento')
                    .then(() => tourManager.moveNext())
                    .catch(() => tourManager.moveNext())
            },
        },
    },
    {
        element: '[data-tour="grid-novo-btn"]',
        ensureReady: () => tourManager.waitForElement(LISTA_READY),
        skipStage: skipStageConfig('paramLiberada'),
        popover: {
            title: 'Demonstração: Equipamento',
            description:
                'Agora vamos cadastrar um equipamento MQTT com dados de exemplo. O preenchimento será realizado automaticamente.<br><br>' +
                '<strong>Pular etapa</strong> conclui este cadastro automaticamente e avança para a parametrização.',
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
                tourManager.navigateAndWait(demoRoutes.equipamentoNovo, '[data-tour="form-imei"]', 3, 'demo-equipamento')
                    .then(() => tourManager.moveNext())
                    .catch(() => {})
            },
        },
    },
    {
        element: '[data-tour="form-imei"]',
        popover: {
            title: 'IMEI',
            description: 'Aqui será preenchido o IMEI, ou numero Identificador único do seu equipamento na plataforma.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillImei()
                await delay(200)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="form-fabricante"]',
        popover: {
            title: 'Fabricante e modelo',
            description: 'Fabricante e modelo  do seu equipamento. Neste passo preenchemos os dois juntos com opções fictícias.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                await fillFabricanteModelo()
                await delay(200)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="form-comunicacao"]',
        popover: {
            title: 'Operadora',
            description: 'Operadora do chip, ou via de comunicação do dispositivo.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                await fillOperadora()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="form-status"]',
        popover: {
            title: 'Status',
            description: 'Ativo: para quando o equipamento for consumir um acesso. Bloqueado: impede a comunicação. Cancelado: remove o acesso.',
            side: 'top',
            align: 'start',
            onNextClick: async () => {
                ensureStatusAtivo()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="form-tipo-acesso"]',
        ensureReady: async () => {
            ensureStatusAtivo()
            await delay(100)
            return tourManager.waitForElement('[data-tour="form-tipo-acesso"]')
        },
        popover: {
            title: 'Tipo do Acesso',
            description: 'O acesso define quantos parâmetros o equipamento pode ter e com que frequência os dados serão apresentados na plataforma. Os acessos são contratados conosco e seguem o formato <strong>parâmetros/tempo</strong>.<br><br>Por exemplo, um acesso <strong>1/5</strong> permite 1 parâmetro e apresenta seus dados a cada 5 minutos. Mesmo que o equipamento envie dados com mais frequência, a plataforma respeita o intervalo definido pelo acesso.<br><br>Somente equipamentos com status <strong>Ativo</strong> podem ter um tipo de acesso definido.',
            side: 'top',
            align: 'start',
            onNextClick: async () => {
                await fillTipoAcesso()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="form-timezone"]',
        popover: {
            title: 'Timezone',
            description: 'Fuso horário usado como referência dos timestamps dos pacotes recebidos.',
            side: 'top',
            align: 'start',
            onNextClick: async () => {
                await fillTimezone()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="tab-protocolo"]',
        popover: {
            title: 'Aba Protocolo',
            description: 'Agora vamos à aba de protocolo para definir o protocolo de comunicação do seu equipamento na plataforma.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                await selectMqttProtocol()
                await delay(200)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="protocol-mqtt"]',
        ensureReady: async () => {
            clickTab('[data-tour="tab-protocolo"]')
            selectProtocol('mqtt')
        },
        popover: {
            title: 'Protocolo MQTT',
            description: 'Nesta demonstração vamos cadastrar um equipamento com protocolo <strong>MQTT</strong>.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        element: '[data-tour="mqtt-topicos-personalizados"]',
        ensureReady: async () => {
            clickTab('[data-tour="tab-protocolo"]')
            selectProtocol('mqtt')
        },
        popover: {
            title: 'Tópicos personalizados',
            description: 'Marcamos <strong>Tópicos Personalizados</strong> para definir manualmente os tópicos de publicação e recepção. Caso contrário, a plataforma gerará esses tópicos automaticamente ao salvar o equipamento.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                enableTopicosPersonalizados()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="mqtt-topico-publicar"]',
        ensureReady: async () => {
            enableTopicosPersonalizados()
            await delay(80)
        },
        popover: {
            title: 'Tópicos Pub e Sub',
            description: 'Nessa Demo, vamos preencher automaticamente os tópicos de Publicação e Recepção do equipamento.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillTopicosMqtt()
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="equipamento-salvar"]',
        popover: {
            title: 'Salvar equipamento',
            description: 'Vamos salvar o equipamento e seguir automaticamente para a Parametrização.',
            side: 'top',
            align: 'end',
            nextBtnText: 'Salvar e continuar',
            onNextClick: () => {
                tourManager._hdvSavingEquipamento = false
                saveEquipamentoAndContinue(tourManager)
            },
        },
        onHighlighted: (element) => {
            tourManager._hdvSavingEquipamento = false
            bindNativeEquipamentoSaveToTour(element, tourManager)
        },
        onDeselected: (element) => unbindNativeEquipamentoSaveFromTour(element),
    },
    {
        popover: {
            title: 'Continuando…',
            description: 'Salvando e abrindo a parametrização. Aguarde um momento.',
            showButtons: ['close'],
        },
    },
]

export default buildDemoEquipamentoTourSteps
